// src/components/shared/FormField.jsx
import React from 'react';
import { styles } from '../../styles/theme';

/**
 * Reusable Input Field Component
 */
export const Input = ({ 
  label, 
  error, 
  required = false,
  ...props 
}) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '0.25rem' 
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      <input
        style={{
          ...styles.input,
          marginBottom: 0,
          borderColor: error ? '#ef4444' : '#d1d5db'
        }}
        {...props}
      />
      {error && (
        <p style={{ 
          color: '#ef4444', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          marginBottom: 0
        }}>
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Reusable Select Field Component
 */
export const Select = ({ 
  label, 
  options = [], 
  error,
  required = false,
  placeholder = 'Please Select',
  ...props 
}) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '0.25rem' 
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      <select
        style={{
          ...styles.select,
          marginBottom: 0,
          borderColor: error ? '#ef4444' : '#d1d5db'
        }}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option 
            key={index} 
            value={typeof option === 'string' ? option : option.value}
          >
            {typeof option === 'string' ? option : option.label}
          </option>
        ))}
      </select>
      {error && (
        <p style={{ 
          color: '#ef4444', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          marginBottom: 0
        }}>
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Reusable Textarea Field Component
 */
export const Textarea = ({ 
  label, 
  error,
  required = false,
  rows = 3,
  ...props 
}) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '0.25rem' 
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      <textarea
        rows={rows}
        style={{
          ...styles.textarea,
          marginBottom: 0,
          borderColor: error ? '#ef4444' : '#d1d5db'
        }}
        {...props}
      />
      {error && (
        <p style={{ 
          color: '#ef4444', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          marginBottom: 0
        }}>
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Checkbox Field Component
 */
export const Checkbox = ({ 
  label, 
  error,
  children,
  ...props 
}) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        cursor: 'pointer',
        gap: '0.5rem'
      }}>
        <input
          type="checkbox"
          style={{ 
            marginTop: '0.25rem',
            width: '18px',
            height: '18px',
            cursor: 'pointer'
          }}
          {...props}
        />
        <span style={{ 
          fontSize: '0.875rem', 
          color: '#374151',
          flex: 1
        }}>
          {children || label}
        </span>
      </label>
      {error && (
        <p style={{ 
          color: '#ef4444', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          marginLeft: '1.625rem',
          marginBottom: 0
        }}>
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Radio Group Component
 */
export const RadioGroup = ({ 
  label, 
  options = [],
  name,
  value,
  onChange,
  error,
  required = false
}) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '0.5rem' 
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      <div>
        {options.map((option, index) => (
          <label 
            key={index}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '0.5rem', 
              cursor: 'pointer',
              gap: '0.5rem'
            }}
          >
            <input
              type="radio"
              name={name}
              value={typeof option === 'string' ? option : option.value}
              checked={value === (typeof option === 'string' ? option : option.value)}
              onChange={onChange}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem' }}>
              {typeof option === 'string' ? option : option.label}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p style={{ 
          color: '#ef4444', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          marginBottom: 0
        }}>
          {error}
        </p>
      )}
    </div>
  );
};

const FormField = { Input, Select, Textarea, Checkbox, RadioGroup };
export default FormField;